<script setup lang="ts">
import axios from 'axios'
import { CircleDot, Plus } from 'lucide-vue-next'

import useLegacyTemplates from '@/views/autopilot/composables/legacyTemplate'
import useContentScopeList, {
  useContentScopeCriteria,
  useContentScopeSearch,
  useContentScopeStatusCriterion
} from '@/composables/contentScopeList'
import type { ContentCreator, ContentScope } from '@/types/contentScope'
import { contentScopeListParams, splitContentScopeListParams } from '@/utils/contentScope'
import { storeParams } from '@/utils/storeParams'

import MarketingFlowItems from '@/views/autopilot/MarketingFlowItems.vue'
import MarketingFlowItemsLegacy from '@/views/autopilot/MarketingFlowItemsLegacy.vue'
import MarketingFlowTooltipConversion from '@/views/autopilot/Components/MarketingFlowTooltipConversion.vue'
import MarketingFlowTooltipRevenue from '@/views/autopilot/Components/MarketingFlowTooltipRevenue.vue'
import MarketingFlowTooltipClick from '@/views/autopilot/Components/MarketingFlowTooltipClick.vue'
import MarketingFlowTooltipExecution from '@/views/autopilot/Components/MarketingFlowTooltipExecution.vue'
import MarketingFlowTemplate from '@/views/autopilot/Components/MarketingFlowTemplates.vue'

export type Trigger = {
  id: number
  type: string
  merchant_segment_id: number | null
}
export type Store = {
  id: number
  name: string
}
export type Statistics = {
  count_messages: number
  count_clicks: number
  count_click_conversions: number
  count_visit_conversions: number
  count_reward_scan_conversions: number
  count_total_conversions: number
  click_conversion_percentage: number
  visit_conversion_percentage: number
  reward_scan_conversion_percentage: number
  total_conversion_percentage: number
  revenue: number
  revenue_formatted: string
  generated_revenue: number
  estimated_revenue: number
  email_sent: number
  email_delivered: number
  email_opened: number
  email_clicked: number
  email_bounced: number
  email_soft_bounced: number
  email_hard_bounced: number
  email_spam_complaints: number
  email_opened_unique: number
  email_clicked_unique: number
  sms_sent: number
  sms_delivered: number
  sms_failed: number
}
export type MarketingFlow = {
  id: number
  updated_at: string
  public_id: string
  name: string
  is_active: boolean
  is_draft: boolean
  is_legacy: boolean
  legacy_trigger: string
  conversion_delay_in_minutes: number
  action_delay_in_minutes: number
  action_delay_time: string
  action_delay_unit: string
  triggers: Trigger[]
  stores: Store[]
  statistics: Statistics
  scope: ContentScope | null
  creator: ContentCreator | null
}

const { stores, isSupportConnection } = useLocalStorage()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { formatNumber, formatPercentage } = useNumber()
const { selectedStoreFilter, updateStores } = useStore()

const isLoading = ref(true)
const isFetching = ref(false)
const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])
const currentTab = ref((route?.query?.tab as string) || 'new')

const pageTitle = ref({
  title: 'pages.autopilot.autopilotsPage.title',
  description: 'pages.autopilot.autopilotsPage.description'
})

const marketingFlows = ref<MarketingFlow[]>([])
const legacyMarketingFlows = ref<MarketingFlow[]>([])

const pagination = ref({
  currentPage: 1,
  lastPage: 1,
  total: 1,
  perPage: 15
})

const statistics = ref<Statistics>({
  count_messages: 0,
  count_clicks: 0,
  count_click_conversions: 0,
  count_visit_conversions: 0,
  count_reward_scan_conversions: 0,
  count_total_conversions: 0,
  click_conversion_percentage: 0,
  visit_conversion_percentage: 0,
  reward_scan_conversion_percentage: 0,
  total_conversion_percentage: 0,
  revenue: 0,
  revenue_formatted: '-',
  generated_revenue: 0,
  estimated_revenue: 0,
  email_sent: 0,
  email_delivered: 0,
  email_opened: 0,
  email_clicked: 0,
  email_bounced: 0,
  email_soft_bounced: 0,
  email_hard_bounced: 0,
  email_spam_complaints: 0,
  email_opened_unique: 0,
  email_clicked_unique: 0,
  sms_sent: 0,
  sms_delivered: 0,
  sms_failed: 0
})

const hasStatistics = computed(() => {
  return (
    statistics.value.count_messages > 0 ||
    statistics.value.count_visit_conversions > 0 ||
    statistics.value.count_click_conversions > 0 ||
    statistics.value.revenue > 0
  )
})

const marketingFlowTabs = computed(() => {
  if (showLegacyFlows.value) {
    return [
      {
        value: 'new',
        name: t('pages.autopilot.autopilotsPage.tabs.new.title')
      },
      {
        value: 'legacy',
        name: t('pages.autopilot.autopilotsPage.tabs.legacy.title')
      }
    ]
  } else {
    return [
      {
        value: 'new',
        name: t('pages.autopilot.autopilotsPage.tabs.new.title')
      }
    ]
  }
})

const fetchAnalytics = async () => {
  const params: {
    store_ids?: number[]
  } = {}

  const isAllStoresSelected = storeFilter.value.length === stores.value.length
  if (storeFilter.value.length > 0 && !isAllStoresSelected) params.store_ids = storeFilter.value

  const { data } = await axios.get<{
    data: Statistics
  }>(`/marketing-flows/statistics`, {
    params
  })
  statistics.value = data.data
}
const toFlowParams = (params: Record<string, unknown>) => {
  const { statuses, search, scopeParams } = splitContentScopeListParams(params)

  return {
    ...storeParams(storeFilter.value, stores.value),
    ...scopeParams,
    ...(search && { name: search }),
    ...(statuses[0] === 'active' && { is_active: 1 }),
    ...(statuses[0] === 'inactive' && { is_active: 0 })
  }
}

const fetchMarketingFlowsTotal = async (scopeParams: Record<string, unknown>) => {
  const { data } = await axios.get<{ meta: { total: number } }>(`/marketing-flows`, {
    params: { ...toFlowParams(scopeParams), per_page: 1 }
  })
  return data.meta.total
}

const fetchMarketingFlows = async (page = 1) => {
  const params: {
    include: string
    store_ids?: number[]
    page?: number
    per_page?: number
  } = {
    include: 'statistics',
    page: page,
    per_page: pagination.value.perPage,
    ...toFlowParams(contentScopeListParams(contentScope.filters.value))
  }

  return await axios
    .get<{
      data: MarketingFlow[]
      meta: { current_page: number; last_page: number; total: number }
    }>(`/marketing-flows`, {
      params
    })
    .then((res) => res.data)
}
const fetchLegacyMarketingFlows = async () => {
  return await axios
    .get<{
      data: MarketingFlow[]
    }>(`/marketing-flows`, {
      params: { only_legacy: 1, include: 'statistics' }
    })
    .then((res) => res.data)
}
const fetchListing = async (page = 1) => {
  try {
    isFetching.value = true

    const [marketingFlowsData, legacyMarketingFlowsData] = await Promise.all([
      fetchMarketingFlows(page),
      fetchLegacyMarketingFlows()
    ])

    marketingFlows.value = marketingFlowsData.data.filter((flow) => !flow.is_legacy)
    legacyMarketingFlows.value = legacyMarketingFlowsData.data

    const total = marketingFlowsData.meta.total
    const currentPage = marketingFlowsData.meta.current_page
    const lastPage = Math.ceil(total / (pagination?.value?.perPage ?? 15))

    pagination.value = {
      ...pagination.value,
      currentPage: currentPage,
      lastPage: lastPage,
      total: total
    }
  } finally {
    isFetching.value = false
  }
}

const contentScope = useContentScopeList('automation', {
  fetchTotal: fetchMarketingFlowsTotal,
  reload: () => fetchListing(1),
  countsKey: () => storeFilter.value
})

const { searchText: searchDraft } = useContentScopeSearch(contentScope)

const { origin, createdBy } = useContentScopeCriteria(contentScope)
const flowStatusValues = ['active', 'inactive'] as const
const statusCriterion = useContentScopeStatusCriterion(contentScope, () => ({
  label: t('pages.campaign.filters.status.label'),
  icon: CircleDot,
  options: flowStatusValues.map((value) => ({ value, label: t(`pages.autopilot.autopilotsPage.items.${value}`) })),
  multiple: false
}))
const filterCriteria = computed(() => [origin.value, createdBy.value, statusCriterion.value])

const showLegacyFlows = computed(() => {
  const hasActiveLegacyFlows = legacyMarketingFlows.value.some((flow) => flow.is_active)
  const hasLegacyFlowsWithStatistics = legacyMarketingFlows.value.some(
    (flow) =>
      flow.statistics.count_clicks > 0 ||
      flow.statistics.count_messages > 0 ||
      flow.statistics.revenue > 0 ||
      flow.statistics.count_click_conversions > 0 ||
      flow.statistics.count_reward_scan_conversions > 0 ||
      flow.statistics.count_total_conversions > 0 ||
      flow.statistics.count_visit_conversions > 0
  )

  return !isSupportConnection.value || hasActiveLegacyFlows || hasLegacyFlowsWithStatistics
})

const goToCreate = () => {
  router.push({ name: 'MarketingFlowCreate' })
}

const fetchData = async (showFullLoader = false) => {
  if (showFullLoader) isLoading.value = true
  await Promise.all([fetchListing(), fetchAnalytics()])
  isLoading.value = false
}

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  await fetchData(false)
})

const onTabChange = (tab: string) => router.push({ query: { tab } })

const marketingFlowsEmpty = computed(
  () => !isFetching.value && marketingFlows.value.length === 0 && !contentScope.hasActiveFilters.value
)
const { formattedLegacyTemplates } = useLegacyTemplates({
  legacyMarketingFlows
})

const goToTemplate = () => {
  router.push({
    name: 'MarketingFlowTemplates'
  })
}

const userStoreIds = computed(() => stores.value.map((s) => s.id))

onMounted(async () => {
  try {
    await updateStores()
  } catch {
    // non-blocking: badge accuracy may use stale localStorage stores
  }
  await fetchData(true)
})
</script>

<template>
  <div>
    <TheLoadingPage v-if="isLoading" />
    <div v-else>
      <div>
        <TheListHeader :title="t(pageTitle.title)">
          <template #actions>
            <Button variant="default-primary" @click="goToCreate()">
              <Plus />
              {{ t('pages.autopilot.autopilotsPage.newScenario') }}
            </Button>
          </template>
        </TheListHeader>

        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <MarketingFlowTemplate class="mb-12" />

          <div class="mb-6 flex items-center">
            <MultiSelectStores v-model="storeFilter" class="mr-4" />

            <div class="flex items-center gap-2 text-blue-600">
              <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5" />

              <span class="text-sm">
                {{ t('global.stats.upgradeAt') }}
              </span>
            </div>
          </div>

          <dl v-if="hasStatistics" class="mb-6 mt-5 grid grid-cols-1 gap-5 md:grid-cols-4">
            <AppStatsCard>
              <template #title>
                <div class="flex items-center gap-2">
                  <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotsPage.data.executions') }}
                  </span>
                  <MarketingFlowTooltipExecution />
                </div>
              </template>
              <template #content>
                <div class="flex flex-col flex-wrap items-baseline justify-between gap-1 md:flex-row">
                  <span class="text-3xl font-extrabold text-primary">
                    {{ formatNumber(statistics.count_messages) }}
                  </span>
                </div>
              </template>
            </AppStatsCard>

            <AppStatsCard>
              <template #title>
                <div class="flex items-center gap-2">
                  <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotsPage.data.visits') }}
                  </span>
                  <MarketingFlowTooltipConversion />
                </div>
              </template>
              <template #content>
                <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
                  <span class="text-3xl font-extrabold text-primary">
                    {{ statistics.count_visit_conversions ? formatNumber(statistics.count_visit_conversions) : '-' }}
                  </span>
                  <span v-if="statistics.count_visit_conversions > 0" class="text-muted-foreground">
                    {{
                      statistics.visit_conversion_percentage
                        ? formatPercentage(statistics.visit_conversion_percentage, 0)
                        : '-'
                    }}
                  </span>
                </div>
              </template>
            </AppStatsCard>
            <AppStatsCard>
              <template #title>
                <div class="flex items-center gap-2">
                  <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotsPage.data.clicks') }}
                  </span>
                  <MarketingFlowTooltipClick />
                </div>
              </template>
              <template #content>
                <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
                  <span class="text-3xl font-extrabold text-primary">
                    {{ statistics.count_click_conversions ? formatNumber(statistics.count_click_conversions) : '-' }}
                  </span>
                  <span v-if="statistics.count_click_conversions > 0" class="text-muted-foreground">
                    {{
                      statistics.click_conversion_percentage
                        ? formatPercentage(statistics.click_conversion_percentage, 0)
                        : '-'
                    }}
                  </span>
                </div>
              </template>
            </AppStatsCard>
            <AppStatsCard>
              <template #title>
                <div class="flex items-center gap-2">
                  <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotsPage.data.revenue') }}
                  </span>
                  <MarketingFlowTooltipRevenue />
                </div>
              </template>
              <template #content>
                <div class="flex flex-col flex-wrap items-baseline justify-between gap-1 md:flex-row">
                  <span class="text-3xl font-extrabold text-primary">
                    {{ statistics.revenue_formatted || '-' }}
                  </span>
                </div>
              </template>
            </AppStatsCard>
          </dl>

          <BaseTab
            v-model="currentTab"
            :tabs="marketingFlowTabs"
            :hide-tabs="!showLegacyFlows"
            @update:model-value="onTabChange"
          >
            <template #new>
              <BaseTip
                v-if="showLegacyFlows"
                class="mb-6"
                severity="info"
                :text="t('pages.autopilot.autopilotsPage.tabs.new.description')"
              />

              <Card v-if="marketingFlowsEmpty">
                <CardHeader>
                  <CardTitle>{{ t('pages.marketingFlow.marketingFlowPerformance.table.title') }}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div class="flex flex-col items-center justify-center gap-4 rounded-xl border bg-muted p-7">
                    <BaseIcon name="HeroBoltIconSolid" class="h-6 w-6" />
                    <div class="flex flex-col items-center text-base text-foreground">
                      <span>{{ t('pages.autopilot.autopilotDetailPage.message.emptyState.title') }}</span>
                      <span>{{ t('pages.autopilot.autopilotDetailPage.message.emptyState.description') }}</span>
                    </div>
                    <Button variant="outline" @click="goToTemplate">
                      {{ t('pages.autopilot.autopilotDetailPage.message.emptyState.button') }}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <ListToolbar v-else class="mb-4" :criteria="filterCriteria">
                <template #leading>
                  <ListSearchInput v-model="searchDraft" :placeholder="t('contentScope.filters.search')" />
                </template>
              </ListToolbar>

              <MarketingFlowItems
                v-if="!marketingFlowsEmpty"
                :marketing-flows="marketingFlows"
                with-origin
                :current-page="pagination.currentPage"
                :last-page="pagination.lastPage"
                :total="pagination.total"
                :user-store-ids="userStoreIds"
                :is-fetching="isFetching"
                :pagination-enabled="true"
                @change-page="fetchListing"
              >
                <template #empty-state>
                  <ContentScopeEmptyState
                    :is-mine-view="contentScope.isMineView.value"
                    :subject="contentScope.subject"
                    @reset="contentScope.resetFilters()"
                  />
                </template>
              </MarketingFlowItems>
            </template>
            <template v-if="showLegacyFlows" #legacy>
              <BaseTip
                class="mb-6"
                severity="secondary"
                :text="t('pages.autopilot.autopilotsPage.tabs.legacy.description')"
              />
              <MarketingFlowItemsLegacy
                :marketing-flows="legacyMarketingFlows"
                :templates="formattedLegacyTemplates"
                :current-page="1"
                :last-page="1"
                :total="legacyMarketingFlows.length"
                :user-store-ids="userStoreIds"
                :is-fetching="isFetching"
                :pagination-enabled="false"
              />
            </template>
          </BaseTab>
        </div>
      </div>
    </div>
  </div>
</template>
